import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import Form from 'react-bootstrap/Form';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import { useMicrosoftAuth } from "../context/MicrosoftAuthContext";
import { toast } from 'react-toastify';
import { pdfjs } from 'react-pdf';
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import PdfPreview from '../components/features/submitted/PdfPreview';
import AdvancedOptionsViewSection from '../components/features/submitted/AdvancedOptionsViewSection';
import SubmissionHeader from '../components/features/submitted/SubmissionHeader';
import MergeFieldsSection from '../components/features/submitted/MergeFieldsSection';
import MeetingSection from '../components/features/submitted/MeetingSection';
import SendLaterSection from '../components/features/submitted/SendLaterSection';
import StrataOptionsSection from '../components/features/submitted/StrataOptionsSection';
import WindowLocksSection from '../components/features/submitted/WindowLocksSection';
import SpecialLeviesSection from '../components/features/submitted/SpecialLeviesSection';
import ImportantDatesSection from '../components/features/submitted/ImportantDatesSection';
import EmailInboxSection from '../components/features/submitted/EmailInboxSection';
import SendToSection from '../components/features/submitted/SendToSection';
import CustomRecipientsSection from '../components/features/submitted/CustomRecipientsSection';
import RemindersSection from '../components/features/submitted/RemindersSection';
import AfssSection from '../components/features/submitted/AfssSection';
import ColorPagesSection from '../components/features/submitted/ColorPagesSection';
import WindowLockApprovalSection from '../components/features/submitted/WindowLockApprovalSection';
import NotesSection from '../components/features/submitted/NotesSection';
import { useTheme } from '../context/ThemeContext';
import { getMergeFieldsConfig } from '../api/suggestionService';
import AccessDeniedView from '../components/features/logs/AccessDeniedView';
import { formatDate, formatRemainingTime } from '../utils/formatters';
import { resolveAuthToken, validateAndSetAuthToken } from '../utils/authUtils';


// Configure PDF.js worker source
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;




const SubmittedView = ({ isOpen, toggle }) => {
    const navigate = useNavigate();
    const { id } = useParams(); // Get the submission ID from the URL
    const { jwtToken, accessToken, userTeamData, isItUser } = useMicrosoftAuth();
    const { isDark } = useTheme();
    const [submission, setSubmission] = useState(null);
    const [recipientPdf, setRecipientPdf] = useState('');
    const [loading, setLoading] = useState(true);
    const [timeLeft, setTimeLeft] = useState(0);
    const [colorPages, setColorPages] = useState([]);
    const [accessDenied, setAccessDenied] = useState(null); // Store access denied message
    const [mergeFieldsConfig, setMergeFieldsConfig] = useState([]); // For field labels
    const [loadingMergeFields, setLoadingMergeFields] = useState(false); // Loading state for merge fields config

    // Update SMATA URL state
    const [showUrlModal, setShowUrlModal] = useState(false);
    const [newSmataUrl, setNewSmataUrl] = useState('');
    const [savingUrl, setSavingUrl] = useState(false);

    // PDF state
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfError, setPdfError] = useState(false);

    // Reference to Swiper instance
    const [swiperInstance, setSwiperInstance] = useState(null);

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);



    // Function to handle successful PDF document loading
    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPageNumber(1);
        setPdfError(false);

        // Create slides for each page, if using a single Swiper for all pages
        if (swiperInstance && numPages > 1) {
            // Update the swiper to have the correct number of slides
            swiperInstance.update();
        }
    };

    // Function to handle PDF document load error
    const onDocumentLoadError = (error) => {
        console.error('Error loading PDF:', error);
        setPdfError(true);
    };

    // Function to handle next page
    const goToNextPage = () => {
        if (pageNumber < numPages) {
            setPageNumber(pageNumber + 1);
        }
    };

    // Function to handle previous page
    const goToPrevPage = () => {
        if (pageNumber > 1) {
            setPageNumber(pageNumber - 1);
        }
    };

    // Fetch submission details
    useEffect(() => {
        const fetchSubmission = async () => {
            try {
                const token = resolveAuthToken(jwtToken, accessToken);
                if (!validateAndSetAuthToken(token, navigate, toast)) return;

                const data = new FormData();
                data.append('user_team_id', userTeamData?.id);
                data.append('user_role_id', userTeamData?.user_role_id);
                data.append('user_email', userTeamData?.email);

                userTeamData?.user_group?.forEach(group => {
                    data.append('user_group[]', group);
                });

                if (userTeamData?.branch_manager_branches && userTeamData.branch_manager_branches.length > 0) {
                    userTeamData.branch_manager_branches.forEach(branch => {
                        data.append('branch_manager_branches[]', branch.branch_id);
                    });
                }

                const response = await api.post(`/submissions/${id}`, data);
                const submissionData = response.data.data;

                if (submissionData.mergeFieldsData && typeof submissionData.mergeFieldsData === 'string') {
                    try {
                        submissionData.mergeFieldsData = JSON.parse(submissionData.mergeFieldsData);
                    } catch (e) {
                        console.error('Error parsing mergeFieldsData:', e);
                        submissionData.mergeFieldsData = {};
                    }
                }

                setSubmission(submissionData);
                setColorPages(submissionData.colorPages.split(','));
                if (submissionData.submission_recipients?.length > 0) {
                    setRecipientPdf(submissionData.submission_recipients[0].public_strata_url);
                }
                if (submissionData.status === 'scheduled' || submissionData.status === 'pending') {
                    const now = new Date();
                    let diffMinutes = 0;

                    if (submissionData.send_later && submissionData.scheduled_time) {
                        const scheduledDate = new Date(submissionData.scheduled_time);
                        diffMinutes = (scheduledDate - now) / (1000 * 60);
                    } else {
                        const submissionDate = new Date(submissionData.createdAt);
                        const waitTime = submissionData.planNumber === 'Test' ? 5 : 15;
                        diffMinutes = waitTime - ((now - submissionDate) / (1000 * 60));
                    }

                    if (diffMinutes > 0) {
                        setTimeLeft(diffMinutes);
                    }
                }
            } catch (error) {
                console.error('Error fetching submission:', error);
                if (error.response?.status === 401) {
                    toast.error('Your session has expired. Please login again.');
                    navigate('/login');
                } else if (error.response?.status === 404) {
                    setAccessDenied(error.response?.data?.message || 'Submission not found or access denied');
                } else {
                    toast.error(error.response?.data?.message || 'Failed to fetch submission details');
                }
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchSubmission();
        }
    }, [id, jwtToken, accessToken, navigate]);

    // Update countdown timer every second
    useEffect(() => {
        if (timeLeft <= 0) return;

        const timer = setInterval(() => {
            setTimeLeft(prevTime => {
                const newTime = prevTime - (1 / 60); // Subtract 1 second (1/60 minutes)
                return newTime > 0 ? newTime : 0;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    useEffect(() => {
        const fetchMergeFieldsConfig = async () => {
            if (!submission?.mergeFieldsData || Object.keys(submission.mergeFieldsData).length === 0) {
                return;
            }

            setLoadingMergeFields(true);
            try {
                const configs = await getMergeFieldsConfig();
                setMergeFieldsConfig(configs);
            } catch (configError) {
                console.error('Error fetching merge fields config:', configError);
                setMergeFieldsConfig([]);
            } finally {
                setLoadingMergeFields(false);
            }
        };

        if (submission) {
            fetchMergeFieldsConfig();
        }
    }, [submission]);

    useEffect(() => {
        if (showUrlModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [showUrlModal]);

    // Handle Update SMATA URL
    const handleOpenUrlModal = () => {
        setNewSmataUrl(submission?.public_strata_url || '');
        setShowUrlModal(true);
    };

    const doSaveSmataUrl = async () => {
        if (!newSmataUrl || !/^https?:\/\/.+/.test(newSmataUrl)) {
            toast.error('Please enter a valid URL starting with http:// or https://');
            return;
        }
        setSavingUrl(true);
        try {
            await api.patch(`/submissions/${id}/smata-url`, { new_url: newSmataUrl });
            setSubmission(prev => ({ ...prev, public_strata_url: newSmataUrl }));
            toast.success('SMATA URL updated successfully');
            setShowUrlModal(false);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to update SMATA URL');
        } finally {
            setSavingUrl(false);
        }
    };

    // Handle cancel submission
    const handleCancelSubmission = async () => {
        try {
            const token = resolveAuthToken(jwtToken, accessToken);
            if (!validateAndSetAuthToken(token, navigate, toast)) return;
            await api.put(`/submissions/${id}/status`, {
                status: 'Cancelled',
                user_role_id: userTeamData?.user_role_id,
                user_id: userTeamData?.id,
                user_email: userTeamData?.email
            });

            // Refresh the submission data
            const data = new FormData();
            data.append('user_team_id', userTeamData?.id);
            data.append('user_role_id', userTeamData?.user_role_id);
            data.append('user_email', userTeamData?.email);

            userTeamData?.user_group?.forEach(group => {
                data.append('user_group[]', group);
            });

            const response = await api.post(`/submissions/${id}`, data);
            setSubmission(response.data.data);
            setTimeLeft(0); // Reset timer
            toast.success('Submission cancelled successfully');
        } catch (error) {
            console.error('Error cancelling submission:', error);
            if (error.response?.status === 401) {
                toast.error('Your session has expired. Please login again.');
                navigate('/login');
            } else {
                toast.error('Failed to cancel submission');
            }
        }
    };



    // Download the PDF file
    const downloadPdf = (url) => {
        if (!url) return;

        // Create a temporary anchor element
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = `submission-${id}.pdf`;

        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return (
            <div className={`min-h-screen flex items-center justify-center transition-colors duration-200 ${
                isDark ? 'bg-[#161515] text-white' : 'bg-[#F5F5F5] text-[#072837]'
            }`}>
                <div className="text-center">
                    Loading submission details...
                </div>
            </div>
        );
    }

    if (accessDenied) {
        return <AccessDeniedView isDark={isDark} accessDenied={accessDenied} />;
    }

    if (!submission) {
        return (
            <div className={`min-h-screen flex items-center justify-center transition-colors duration-200 ${
                isDark ? 'bg-[#161515] text-white' : 'bg-[#F5F5F5] text-[#072837]'
            }`}>
                <div className="text-center">
                    Submission not found
                </div>
            </div>
        );
    }

    // Parse JSON content if available
    let jsonContent = {};
    try {
        if (submission.json_content) {
            jsonContent = JSON.parse(submission.json_content);
        }
    } catch (error) {
        console.error('Error parsing JSON content:', error);
    }

    // Helper function to render radio button
    const renderRadioButton = (name, value, label) => {
        const isChecked = submission[name] === value || jsonContent[name] === value;

        if (!isChecked) return null;

        return (
            <div className='d-flex mb-2'>
                <Form.Check
                    className="m-0 d-flex align-items-center text-898989"
                    type="radio"
                    checked={isChecked}
                    readOnly
                    disabled
                />
                <span className='text-14 text-black fw-300 ml-2 text-capitalize'>{label}</span>
            </div>
        );
    };

    // Get displayed sending option text
    const getSendingOptionText = () => {
        const sendTo = submission.sendTo || jsonContent.sendTo;
        const sendingOptions = [
            'All Owners/Residents/Agents',
            'All Owners (service of Notice Address)',
            'All Residents',
            'All Agents',
            'All Owners/Agents',
            'All Residents/Agents',
            'All Owners/Residents',
            'Lot only - specify in next field which lot and to who i.e. Resident/Owner',
            'Committee Members only',
            'Tenants only'
        ];

        return sendingOptions.find(option => option === sendTo) || sendTo;
    };

    const getFileNameFromUrl = (url) => {

        return url.substring(url.lastIndexOf('/') + 1);
    };

    const hasPdf = (submission.public_strata_url && submission.public_strata_url.trim() !== '') || 
                   (submission.status !== 'Cancelled' && submission.mergedPdfName && submission.mergedPdfName.trim() !== '');
    
    const getPdfUrl = () => {
        if (submission.public_strata_url && submission.public_strata_url.trim() !== '') {
            return submission.public_strata_url;
        }
        if (submission.mergedPdfName && submission.mergedPdfName.trim() !== '') {
            const baseUrl = process.env.REACT_APP_API_URL || '';
            return `${baseUrl}/public/${submission.mergedPdfName}`;
        }
        return '';
    };

    return (
        <div className={`container-fluid p-4 sm:p-6 lg:p-24 flex flex-col gap-[40px] sm:gap-[40px] lg:gap-[40px] transition-colors duration-200 ${
            isDark ? 'bg-[#000000]' : 'bg-[#F5F5F5]'
        }`}>
            {showUrlModal && createPortal(
                <div
                    className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
                    style={{ zIndex: 99999 }}
                    onClick={() => setShowUrlModal(false)}
                >
                    <div
                        className={`w-full max-w-[500px] rounded-[16px] p-[24px] flex flex-col gap-[20px] transition-colors duration-200 ${
                            isDark ? 'bg-[#1a1a1a]' : 'bg-white'
                        }`}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between gap-[12px]">
                            <h3 className={`m-0 text-[18px] font-bold transition-colors duration-200 ${
                                isDark ? 'text-white' : 'text-[#1a1a1a]'
                            }`}>Update SMATA URL</h3>
                            <button
                                onClick={() => setShowUrlModal(false)}
                                className={`w-[28px] h-[28px] rounded-full border-0 cursor-pointer flex items-center justify-center shrink-0 transition-colors duration-200 ${
                                    isDark ? 'bg-[#333] text-white hover:bg-[#444]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'
                                }`}
                                aria-label="Close"
                            >
                                <X size={14} />
                            </button>
                        </div>

                        {/* Current URL */}
                        <div className="flex flex-col gap-[6px]">
                            <label className={`text-[14px] font-medium transition-colors duration-200 ${
                                isDark ? 'text-[#b0b0b0]' : 'text-[#686868]'
                            }`}>Current URL</label>
                            <div className={`w-full rounded-[8px] px-[14px] py-[10px] text-[14px] break-all transition-colors duration-200 ${
                                isDark ? 'bg-[#2a2a2a] text-[#888]' : 'bg-[#f5f5f5] text-[#999]'
                            }`}>
                                {submission.public_strata_url || '-'}
                            </div>
                        </div>

                        {/* New URL */}
                        <div className="flex flex-col gap-[6px]">
                            <label className={`text-[14px] font-medium transition-colors duration-200 ${
                                isDark ? 'text-[#b0b0b0]' : 'text-[#686868]'
                            }`}>New URL</label>
                            <input
                                type="url"
                                value={newSmataUrl}
                                onChange={e => setNewSmataUrl(e.target.value)}
                                placeholder="https://..."
                                className={`w-full rounded-[8px] px-[14px] py-[10px] text-[14px] border-solid border-[1px] outline-none focus:border-[#cd6d2c] transition-colors duration-200 ${
                                    isDark
                                        ? 'bg-[#0d0d0d] border-[#292929] text-white placeholder-[#666]'
                                        : 'bg-white border-[#d9d9d9] text-[#1a1a1a] placeholder-[#aaa]'
                                }`}
                            />
                        </div>

                        {/* Actions */}
                        <div className="flex gap-[10px]">
                            <button
                                onClick={doSaveSmataUrl}
                                disabled={savingUrl}
                                className="rounded-[8px] px-[20px] py-[10px] text-[14px] font-semibold border-0 cursor-pointer bg-[#cd6d2c] text-white hover:opacity-90 transition-opacity duration-200 disabled:opacity-50"
                            >
                                {savingUrl ? 'Updating...' : 'Update'}
                            </button>
                            <button
                                onClick={() => setShowUrlModal(false)}
                                className="rounded-[8px] px-[20px] py-[10px] text-[14px] font-semibold border-0 cursor-pointer bg-[#FFF5EB] text-[#cd6d2c] hover:opacity-90 transition-opacity duration-200"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <SubmissionHeader
                submission={submission}
                isDark={isDark}
                userTeamData={userTeamData}
                timeLeft={timeLeft}
                formatDate={formatDate}
                formatRemainingTime={formatRemainingTime}
                handleCancelSubmission={handleCancelSubmission}
                navigate={navigate}
                isItUser={isItUser}
                onUpdateUrl={handleOpenUrlModal}
            />

            <div className={`flex flex-col gap-[9px] items-center rounded-[12px] sm:rounded-[20px] border-solid border-[1px] p-4 sm:p-5 lg:p-[24px] transition-colors duration-200 ${
                isDark ? 'bg-[#161515] border-[#292929]' : 'bg-[#fff5eb] border-[#d9d9d9]'
            }`}>
            <div className='flex flex-col xl:flex-row w-full items-start gap-[24px] xl:gap-[32px]'>
                <div className='w-full xl:w-[55%] flex flex-col gap-[9px]'>
                    {/* Page Section Header */}
                    <div className='flex flex-col gap-[6px]'>
                        <h2 className={`m-0 text-[20px] font-semibold transition-colors duration-200 ${
                            isDark ? 'text-white' : 'text-[#072837]'
                        }`}>Your Submission</h2>
                        <p className={`m-0 text-[16px] transition-colors duration-200 ${
                            isDark ? 'text-[#b0b0b0]' : 'text-[#686868]'
                        }`}>Below are the details of your submission.</p>
                    </div>

                    <StrataOptionsSection submission={submission} jsonContent={jsonContent} isDark={isDark} />

                    <WindowLocksSection submission={submission} jsonContent={jsonContent} isDark={isDark} />

                    <SpecialLeviesSection submission={submission} jsonContent={jsonContent} isDark={isDark} />

                    <ImportantDatesSection submission={submission} jsonContent={jsonContent} isDark={isDark} />

                    <EmailInboxSection submission={submission} isDark={isDark} />

                    {/* Meeting Register - Only for Strata submissions */}
                    <MeetingSection submission={submission} jsonContent={jsonContent} isDark={isDark} />

                    <SendToSection submission={submission} jsonContent={jsonContent} isDark={isDark} getSendingOptionText={getSendingOptionText} />

                    <CustomRecipientsSection submission={submission} isDark={isDark} />

                    <RemindersSection submission={submission} isDark={isDark} />

                    {/* Dynamic Merge Fields - Display all fields from mergeFieldsData */}
                    <MergeFieldsSection submission={submission} mergeFieldsConfig={mergeFieldsConfig} loadingMergeFields={loadingMergeFields} isDark={isDark} />



                    {/* Lot # (Only if 'Lot only' was selected) */}
                    {/* {(submission.sendTo === 'Lot only - specify in next field which lot and to who i.e. Resident/Owner' || 
                      jsonContent.sendTo === 'Lot only - specify in next field which lot and to who i.e. Resident/Owner') && (
                        <div className='bg-white p-16 rounded-16 border mb-16'>
                            <div className='text-14 fw-600 mb-16'>Lot # </div>
                            <div><input className="form-control form-control-lg py-0 w-50 input-field" type="text" value={submission.lotNumber || jsonContent.lotNumber || ''} readOnly /></div>
                            <div className='mt-7 fw-400 text-12 text-898989'>Add multiple Lots and who to.</div>
                        </div>
                    )} */}

                    <AfssSection submission={submission} jsonContent={jsonContent} isDark={isDark} />

                    {/* Minutes For (conditional) */}
                    {/* {(submission.sending === 'Minutes' || jsonContent.sending === 'Minutes') && (
                        <div className='bg-white p-16 rounded-16 border mb-16'>
                            <div className='text-14 fw-600 mb-16'>Minutes for</div>
                            {renderRadioButton('dynamicMinutesSelection', 'AGM', 'AGM')}
                            {renderRadioButton('dynamicMinutesSelection', 'EGM', 'EGM')}
                            {renderRadioButton('dynamicMinutesSelection', 'Other', 'Other')}
                        </div>
                    )} */}

                    {/* Color Pages */}
                    <ColorPagesSection submission={submission} jsonContent={jsonContent} isDark={isDark} />

                    {/* Window Lock Approval (conditional on Minutes) */}
                    <WindowLockApprovalSection submission={submission} jsonContent={jsonContent} isDark={isDark} />

                    {/* Notes */}
                    <NotesSection submission={submission} jsonContent={jsonContent} isDark={isDark} />


                    {/* Advanced Options - Grouped accordions for all submissions */}
                    <AdvancedOptionsViewSection
                        submission={submission}
                        isDark={isDark}
                        showStrataOptions={submission.recipient_source !== 'non-strata'}
                    />

                    {/* Send Later Section */}
                    <SendLaterSection submission={submission} jsonContent={jsonContent} isDark={isDark} />
                </div>

                {/* Files Section */}
                <div className='w-full xl:w-[45%] xl:shrink-0 xl:sticky xl:top-[20px] self-start flex flex-col gap-[12px]'>
                    {hasPdf ? (
                        <PdfPreview
                            pdfUrl={getPdfUrl()}
                            fileName={(submission.public_strata_url && submission.public_strata_url != 'null') ? getFileNameFromUrl(submission.public_strata_url) : (submission.mergedPdfName && submission.mergedPdfName != 'null' ? submission.mergedPdfName : '')}
                            onDocumentLoadSuccess={onDocumentLoadSuccess}
                            onDocumentLoadError={onDocumentLoadError}
                            pageNumber={pageNumber}
                            numPages={numPages}
                            goToPrevPage={goToPrevPage}
                            goToNextPage={goToNextPage}
                            colorPages={colorPages}
                            setSwiperInstance={setSwiperInstance}
                            onDownload={() => downloadPdf(getPdfUrl())}
                            isDark={isDark}
                            pdfError={pdfError}
                        />
                    ) : (
                        <div className={`rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] p-[20px] flex flex-col gap-[16px] items-center justify-center min-h-[400px] transition-colors duration-200 ${
                            isDark ? 'bg-[#000000]' : 'bg-white'
                        }`}>
                            <div className='flex flex-col gap-[16px] items-center'>
                                <div
                                    className="w-[16px] h-[16px]"
                                    style={{
                                        backgroundColor: isDark ? '#cd6d2c' : '#000000',
                                        WebkitMaskImage: "url('/assets/icons/hourglass.svg')",
                                        WebkitMaskRepeat: 'no-repeat',
                                        WebkitMaskSize: 'contain',
                                        WebkitMaskPosition: 'center',
                                        maskImage: "url('/assets/icons/hourglass.svg')",
                                        maskRepeat: 'no-repeat',
                                        maskSize: 'contain',
                                        maskPosition: 'center',
                                        transition: 'background-color 0.2s ease'
                                    }}
                                    aria-hidden="true"
                                />
                                <p className={`m-0 text-[14px] font-semibold transition-colors duration-200 ${
                                    isDark ? 'text-white' : 'text-[#000000]'
                                }`}>
                                    Please Wait...
                                </p>
                                <p className={`m-0 text-[14px] font-light text-center max-w-[266px] transition-colors duration-200 ${
                                    isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
                                }`}>
                                    Please wait while the document is being prepared. It will be available after the cancellation period has ended.
                                </p>
                            </div>
                        </div>
                        // <UploadFile
                        //      files={submission.files || []}
                        //      readOnly={true}
                        //  /> 
                    )}
                </div>
                </div>
            </div>
        </div>
    );
};

export default SubmittedView;
